import { CheckCircle2, HandCoins, XCircle } from "lucide-react";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { Button, Card, Spinner } from "../components/ui";

/**
 * Public page (no auth) for an off-platform loan counterparty to accept or
 * decline a loan request from an emailed link.
 */
export function LoanRespondPage(): JSX.Element {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [state, setState] = useState<"ready" | "busy" | "done" | "error">(token ? "ready" : "error");
  const [outcome, setOutcome] = useState<string | null>(null);

  const respond = async (action: "accept" | "decline"): Promise<void> => {
    setState("busy");
    try {
      const res = await api<{ status: string }>("/loan/respond", {
        method: "POST",
        body: { token, action },
        anonymous: true,
      });
      setOutcome(res.status);
      setState("done");
    } catch {
      setState("error");
    }
  };

  return (
    <div className="flex min-h-full items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <h1 className="text-2xl font-bold text-brand-500">Vouch</h1>
          <p className="text-sm text-slate-500">Loan request</p>
        </div>
        <Card>
          {state === "busy" && <Spinner />}
          {state === "error" && (
            <div className="text-center">
              <XCircle className="mx-auto mb-2 text-red-500" size={32} />
              <p className="text-sm text-slate-600">This link is invalid, expired, or already handled.</p>
            </div>
          )}
          {state === "done" && (
            <div className="text-center">
              {outcome === "rejected" ? (
                <XCircle className="mx-auto mb-2 text-slate-400" size={32} />
              ) : (
                <CheckCircle2 className="mx-auto mb-2 text-green-500" size={32} />
              )}
              <p className="text-sm text-slate-600">
                You have <strong>{outcome === "rejected" ? "declined" : "accepted"}</strong> this loan.
                {outcome !== "rejected" && " Sign up on Vouch to track it."}
              </p>
            </div>
          )}
          {state === "ready" && (
            <div className="space-y-4 text-center">
              <HandCoins className="mx-auto text-brand-500" size={32} />
              <p className="text-sm text-slate-600">
                Someone recorded a loan with you on Vouch. Nothing is finalised until you respond.
              </p>
              <div className="flex gap-2">
                <Button variant="ghost" className="flex-1" onClick={() => void respond("decline")}>
                  Decline
                </Button>
                <Button className="flex-1" onClick={() => void respond("accept")}>
                  Accept
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
